<template>
    <ion-header translucent class="resource-viewer-toolbar">
        <ion-toolbar class="modal-toolbar" mode="md">
            <ion-buttons slot="end">
                <ion-button @click="dismiss">
                    {{ t("messages.application.close") }} 
                    <ion-icon :src="close" style="font-size: 20px"/>
                </ion-button>
            </ion-buttons>
        </ion-toolbar>
    </ion-header>
    
      <ion-content>
        <div v-show="showShareIcon" class="resource-share-container">
            <div class="sub-menu-chiplet resource-menu-share"> 
                <ion-icon class="chiplet-icon" :icon="shareSocial" mode="md" @click="handleShareResource(resource)"/>
            </div>
        </div>
        <div v-if="resource.MimeType === 'application/pdf'" class="pdf-container">
          <pdf-viewer 
            :src="`${resource.ResourceData}`"
            style="height: 100%; width: 100%;margin-top: 10px;"
          />
        </div>
        <div v-else-if="resource.MimeType === 'audio/mpeg'" class="audio-container">
            <h3>{{ resource.Name }}</h3>
            <ion-icon :icon="musicalNotes" style="font-size: 200px; color: var(--ion-color-dark); margin-bottom: 10px;"></ion-icon>
            <audio-player 
                :audio-data="`${resource.ResourceData}`" 
                :start-time-seconds="0" 
                :end-time-seconds="0" 
                :class="'no-nav'"
                color="--color-primary">
            </audio-player>
        </div>
        <div v-else-if="resource.MimeType === 'text/html' || 'text/x-uri'" class="iframe-container">
            <iframe 
                :src="`${resource.Url}`" 
                width="100%" 
                height="100%"
                frameborder="0" 
                marginheight="0" 
                marginwidth="0">
                Loading Form...
            </iframe>
        </div>
        <div v-else>
            {{ resource?.mimeType }}
        </div>

        
      </ion-content>
  </template>
  
  <script lang="ts">
  import { computed, defineComponent, ref ,onMounted} from 'vue';
  import { useStore } from "vuex";
  import { AttachedResource } from '@/storage/DataStorage';
  import { IonButton, IonContent, IonHeader, IonToolbar, IonButtons,IonIcon } from '@ionic/vue';
  import PdfViewer from 'pdf-vue3';
  import AudioPlayer from '@/components/AudioPlayer.vue';
  import { close, musicalNotes , shareSocial } from "ionicons/icons";
  import {FileIdentifiers, MimeTypes} from "@/types/types";
  import translate from "../views/translate";
  import {shareFile, writeFile} from "@/utilities/Filesystem";
  import { shareAudio, shareAudioFile , shareUrl} from '@/services/ShareService';
  import { ActionSheetBuilder } from '@/utilities/ActionSheetBuilder';
  
  export default defineComponent({
    name: 'ResourceViewerModal',
    components: {
      IonButton,
      IonContent,
      IonHeader,
      IonToolbar,
      IonButtons,
      IonIcon,
      PdfViewer,
      AudioPlayer
    },
    props: {
        dismiss: { type: Function },
        resource: { type: Object as () => AttachedResource }
    },
    setup(props) {
        const base64Pdf = computed(() => { return props.resource ? true : false});
        const store = useStore();
        const localeMessages = computed(() => store.getters.getLocaleMessages());
        const { t } = translate();
        const resourceTitle = computed(() => { return props.resource ? props.resource.Name : ''});
        const showShareIcon = computed(() => { return (props.resource  && (props.resource.Shareable || props.resource.MimeType === MimeTypes.PDF) && localeMessages.value.buildConfigs.config.shareUnencryptedFunctionality) ? true : false});

        const sharePdf = async () => {
            if(props.resource && props.resource.ResourceData){
                const pdfFileName = `${FileIdentifiers.Pdf}${props.resource.Name}`;
                const base64Pdf = props.resource.ResourceData.replace("data:application/pdf;base64,", "");
                await shareFile(pdfFileName.replace(".pdf", "") + ".pdf", base64Pdf, false, 'application/pdf');
            }
        }

        const savePdf = async () => {
            if(props.resource && props.resource.ResourceData){
                let pdfFileName = `${FileIdentifiers.Pdf}${resourceTitle.value}`;
                pdfFileName = pdfFileName.replace(".pdf","") + ".pdf";
                const base64Pdf = props.resource.ResourceData.replace("data:application/pdf;base64,", "");  
                await writeFile(pdfFileName, base64Pdf, false, MimeTypes.PDF, false);
            }
        }

        const saveAudio = async () => {
            if(props.resource && props.resource.ResourceData){
                const audioFileName = `${FileIdentifiers.Audio}${resourceTitle.value}.mp3`;
                const base64Audio = props.resource.ResourceData.replace("data:audio/mpeg;base64,", "");
                await writeFile(audioFileName.replace(".mp3", ""), base64Audio, false, MimeTypes.AUDIO, false);
            }
        }
        const handleShareResource = async () => {
            if(props.resource ){
                if(props.resource.MimeType === MimeTypes.TEXT || props.resource.MimeType === MimeTypes.URL){
                    if(props.resource.Url){
                        await shareUrl(props.resource.Url);
                    }
                }else{
                    const resourceName = props.resource.Name || '';
                    const resourceData = props.resource.ResourceData || '';
                    try {
                        const actionSheetBuilder = new ActionSheetBuilder(props.resource.Name);
                        if (t("messages.buildConfigs.config.shareUnencryptedFunctionality") === "true") {
                            if (store.getters.isPwa()) {
                                if(props.resource.MimeType === MimeTypes.PDF){
                                    actionSheetBuilder
                                        .addSavePdf(savePdf);
                                }else if(props.resource.MimeType === MimeTypes.AUDIO){
                                    actionSheetBuilder
                                        .addSaveAudio(saveAudio);
                                }
                            } else {
                                if(props.resource.MimeType === MimeTypes.PDF){
                                    actionSheetBuilder
                                        .addSavePdf(savePdf)
                                        .addShareWithOthers( async() =>{
                                            await sharePdf();
                                        });
                                }else if(props.resource.MimeType === MimeTypes.AUDIO){
                                    actionSheetBuilder
                                        .addSaveAudio(saveAudio)
                                        .addShareWithOthers(async () => {
                                            await shareAudioFile(resourceName, resourceData);
                                        });
                                }
                            }
                        }
                        actionSheetBuilder.addClose();
                        const actionSheet = await actionSheetBuilder.create();
                        await actionSheet.present();
                    } catch {
                        return;
                    }
                }
            }
        }

        return {
            base64Pdf,
            close,
            t,
            resourceTitle,
            musicalNotes,
            shareSocial,
            showShareIcon,
            handleShareResource
        };
}
  });
  </script>
  
  <style scoped>
    .audio-container {
        display: flex;
        flex-direction: column;
        align-items: center; 
        height: 100%; 
        width: 100%; 
        padding: 10px; 
        box-sizing: border-box; 
        background-color: white;
    }

    .audio-player-compact {
        width: 95%;
        padding: 10px 25px 10px 25px;
        margin-bottom: calc(80px + var(--ion-safe-area-bottom));
    }

    button {
        background: #007bff;
        color: white;
        border: none;
        padding: 8px 12px;
        border-radius: 5px;
        cursor: pointer;
    }

    button:hover {
        background: #0056b3;
    }

    input[type="range"] {
        width: 80%;
        color: black;
    }
  
    .pdf-container {
        height: 100%;
        width: 100%;
        overflow: hidden;
    }
    
    .iframe-container {
        height: 100%;
        width: 100%;
        overflow: hidden;
    }
    

    p {
        text-align: center;
        font-size: 1.2rem;
        margin-top: 2rem;
    }

    .resource-share-container {
        display:flex;
        justify-content: center;
        align-items: center;
        background-color: white;
    }

    .sub-menu-chiplet.resource-menu-share {
        margin:0px auto 8px auto;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor:pointer;
        width:42px;
        height:42px;
        /*                  background-color: white!important; */
    }

    .resource-menu-share ion-icon {
        margin:0;
    }

    .resource-title {
        font-size: 1.5rem;
        text-align: center;
        margin-left:auto;
        margin-right:auto;
    }

    .modal-toolbar {
        padding-top: 0;
    }
</style>
  